import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { createAdminAccount, deleteAdminAccount, getAllAdmins, updateAdminAccount } from '../../services/authService';
import './AdminManagement.css';

const AdminManagement = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [newAdmin, setNewAdmin] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: ''
  });
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadAdmins();
  }, []);

  const loadAdmins = async () => {
    try {
      const adminList = await getAllAdmins();
      setAdmins(adminList);
    } catch (error) {
      setError('Failed to load admin accounts');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewAdmin(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditingAdmin(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getPasswordStrength = (password) => {
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^a-zA-Z0-9]/.test(password)) score++;

    if (score <= 1) return { label: 'Weak', color: '#dc2626', percent: 20 };
    if (score === 2) return { label: 'Fair', color: '#f59e0b', percent: 40 };
    if (score === 3) return { label: 'Good', color: '#eab308', percent: 60 };
    if (score === 4) return { label: 'Strong', color: '#22c55e', percent: 80 };
    return { label: 'Very Strong', color: '#16a34a', percent: 100 };
  };

  const passwordStrength = getPasswordStrength(newAdmin.password);
  const editPasswordStrength = getPasswordStrength(editingAdmin?.password || '');

  const validatePassword = (password, confirmPassword, requirePassword = true) => {
    if (!password && !requirePassword) {
      return null;
    }

    if (password.length < 8) {
      return 'Password must be at least 8 characters';
    }

    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }

    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }

    if (!/\d/.test(password)) {
      return 'Password must contain at least one number';
    }

    if (password !== confirmPassword) {
      return 'Passwords do not match';
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const passwordError = validatePassword(newAdmin.password, newAdmin.confirmPassword, true);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    try {
      await createAdminAccount({
        email: newAdmin.email,
        password: newAdmin.password,
        name: newAdmin.name
      });
      setSuccess('Admin account created successfully');
      setNewAdmin({ email: '', password: '', confirmPassword: '', name: '' });
      loadAdmins();
    } catch (error) {
      setError(error.message);
    }
  };

  const startEditingAdmin = (admin) => {
    setError('');
    setSuccess('');
    setIsResettingPassword(false);
    setEditingAdmin({
      id: admin.id,
      email: admin.email,
      password: '',
      confirmPassword: '',
      name: admin.name || ''
    });
  };

  const cancelEditingAdmin = () => {
    setEditingAdmin(null);
    setIsResettingPassword(false);
  };

  const handleUpdateAdmin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const passwordError = validatePassword(
      editingAdmin.password,
      editingAdmin.confirmPassword,
      isResettingPassword
    );
    if (passwordError) {
      setError(passwordError);
      return;
    }

    try {
      const updatedAdmin = await updateAdminAccount(editingAdmin.id, {
        email: editingAdmin.email,
        password: editingAdmin.password,
        name: editingAdmin.name
      });

      setAdmins(prev =>
        prev.map(admin => (admin.id === updatedAdmin.id ? updatedAdmin : admin))
      );
      setSuccess('Admin account updated successfully');
      setEditingAdmin(null);
      setIsResettingPassword(false);
    } catch (error) {
      setError(error.message);
    }
  };

  const handleDeleteAdmin = async (adminId) => {
    setError('');
    setSuccess('');

    const adminToDelete = admins.find(admin => admin.id === adminId);
    if (!adminToDelete) {
      return;
    }

    if (!window.confirm(`Delete admin user ${adminToDelete.email}?`)) {
      return;
    }

    try {
      await deleteAdminAccount(adminId);
      setAdmins(prev => prev.filter(admin => admin.id !== adminId));
      if (editingAdmin?.id === adminId) {
        setEditingAdmin(null);
        setIsResettingPassword(false);
      }
      if (currentUser?.id === adminId) {
        await logout();
        navigate('/admin', { replace: true });
        return;
      }
      setSuccess('Admin account deleted successfully');
    } catch (error) {
      setError(error.message);
    }
  };

  if (currentUser?.role !== 'admin') {
    return <div>Access denied</div>;
  }

  return (
    <div className="admin-management">
      <h2>Admin Management</h2>
      
      {!editingAdmin && (
        <div className="admin-form-container">
          <h3>Create New Admin</h3>
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}
          
          <form onSubmit={handleSubmit} className="admin-form">
            <div className="form-group">
              <label>Name:</label>
              <input
                type="text"
                name="name"
                value={newAdmin.name}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label>Email:</label>
              <input
                type="email"
                name="email"
                value={newAdmin.email}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label>Password:</label>
              <input
                type="password"
                name="password"
                value={newAdmin.password}
                onChange={handleInputChange}
                required
                minLength={8}
              />
            </div>

            <div className="form-group">
              <label>Confirm Password:</label>
              <input
                type="password"
                name="confirmPassword"
                value={newAdmin.confirmPassword}
                onChange={handleInputChange}
                required
              />
              {newAdmin.confirmPassword.length > 0 && newAdmin.password !== newAdmin.confirmPassword && (
                <span className="password-mismatch">Passwords do not match</span>
              )}
              {newAdmin.confirmPassword.length > 0 && newAdmin.password === newAdmin.confirmPassword && (
                <span className="password-match">Passwords match</span>
              )}
            </div>

            {newAdmin.password.length > 0 && (
              <div className="password-strength">
                <div className="strength-bar">
                  <div
                    className="strength-fill"
                    style={{ width: `${passwordStrength.percent}%`, backgroundColor: passwordStrength.color }}
                  />
                </div>
                <span className="strength-label" style={{ color: passwordStrength.color }}>
                  {passwordStrength.label}
                </span>
              </div>
            )}

            <ul className="password-rules">
              <li className={newAdmin.password.length >= 8 ? 'met' : ''}>At least 8 characters</li>
              <li className={/[A-Z]/.test(newAdmin.password) ? 'met' : ''}>One uppercase letter</li>
              <li className={/[a-z]/.test(newAdmin.password) ? 'met' : ''}>One lowercase letter</li>
              <li className={/\d/.test(newAdmin.password) ? 'met' : ''}>One number</li>
              <li className={/[^a-zA-Z0-9]/.test(newAdmin.password) ? 'met' : ''}>One special character (recommended)</li>
            </ul>
            
            <button type="submit" className="submit-button">
              Create Admin Account
            </button>
          </form>
        </div>
      )}

      {editingAdmin && (
        <div className="admin-form-container">
          <h3>Edit Admin</h3>
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          <form onSubmit={handleUpdateAdmin} className="admin-form">
            <div className="form-group">
              <label>Name:</label>
              <input
                type="text"
                name="name"
                value={editingAdmin.name}
                onChange={handleEditInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Email:</label>
              <input
                type="email"
                name="email"
                value={editingAdmin.email}
                onChange={handleEditInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Password:</label>
              {!isResettingPassword ? (
                <button
                  type="button"
                  className="secondary-button inline-button"
                  onClick={() => setIsResettingPassword(true)}
                >
                  Reset Password
                </button>
              ) : (
                <>
                  <input
                    type="password"
                    name="password"
                    value={editingAdmin.password}
                    onChange={handleEditInputChange}
                    placeholder="Enter new password"
                  />
                  <span className="password-reset-note">Password strength checks are required for new passwords.</span>
                </>
              )}
            </div>

            {isResettingPassword && (
              <div className="form-group">
                <label>Confirm New Password:</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={editingAdmin.confirmPassword}
                  onChange={handleEditInputChange}
                  placeholder="Confirm new password"
                />
                {editingAdmin.confirmPassword.length > 0 && editingAdmin.password !== editingAdmin.confirmPassword && (
                  <span className="password-mismatch">Passwords do not match</span>
                )}
                {editingAdmin.password.length > 0 && editingAdmin.confirmPassword.length > 0 && editingAdmin.password === editingAdmin.confirmPassword && (
                  <span className="password-match">Passwords match</span>
                )}
              </div>
            )}

            {isResettingPassword && editingAdmin.password.length > 0 && (
              <>
                <div className="password-strength">
                  <div className="strength-bar">
                    <div
                      className="strength-fill"
                      style={{ width: `${editPasswordStrength.percent}%`, backgroundColor: editPasswordStrength.color }}
                    />
                  </div>
                  <span className="strength-label" style={{ color: editPasswordStrength.color }}>
                    {editPasswordStrength.label}
                  </span>
                </div>

                <ul className="password-rules">
                  <li className={editingAdmin.password.length >= 8 ? 'met' : ''}>At least 8 characters</li>
                  <li className={/[A-Z]/.test(editingAdmin.password) ? 'met' : ''}>One uppercase letter</li>
                  <li className={/[a-z]/.test(editingAdmin.password) ? 'met' : ''}>One lowercase letter</li>
                  <li className={/\d/.test(editingAdmin.password) ? 'met' : ''}>One number</li>
                  <li className={/[^a-zA-Z0-9]/.test(editingAdmin.password) ? 'met' : ''}>One special character (recommended)</li>
                </ul>
              </>
            )}

            <div className="admin-form-actions">
              <button type="submit" className="submit-button">
                Save Changes
              </button>
              {isResettingPassword && (
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => {
                    setEditingAdmin(prev => ({
                      ...prev,
                      password: '',
                      confirmPassword: ''
                    }));
                    setIsResettingPassword(false);
                  }}
                >
                  Cancel Password Reset
                </button>
              )}
              <button type="button" className="secondary-button" onClick={cancelEditingAdmin}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="admin-list">
        <h3>Existing Admins</h3>
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Created At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {admins.map(admin => (
              <tr key={admin.id}>
                <td>{admin.name}</td>
                <td>{admin.email}</td>
                <td>{new Date(admin.createdAt).toLocaleDateString()}</td>
                <td>
                  <div className="admin-row-actions">
                    <button
                      type="button"
                      className="table-button secondary-button"
                      onClick={() => startEditingAdmin(admin)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="table-button danger-button"
                      onClick={() => handleDeleteAdmin(admin.id)}
                      disabled={admins.length <= 1}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {admins.length <= 1 && (
          <p className="admin-list-note">At least one admin user must remain.</p>
        )}
      </div>
    </div>
  );
};

export default AdminManagement;
